import logger, { HOST_REPLY_TYPE, installHostListener } from '@goldcast/logger';
import { uicomponents } from '@goldcast/uicomponents-branding';
import { Amplify, Auth } from 'aws-amplify';
import { createApp } from 'vue';

import App from './App.vue';
import globalComponents from './components';
import clickOutsideDirective from './directives/clickOutside';
import router from './router';
import { initUnauthedUserSession } from './services/unauthed.service';
import initializeFetchers from './store/fetchRegister';
import { deleteWrongCognitoDomainCookies } from './utils/cookies';

import store from '@/store';
import { getEnv } from '@/utils/env';
import { getCookieValue } from '@/utils/utils.js';

const app = createApp(App);

app.use(store);
app.use(router);

// Initialize the fetch interceptors
initializeFetchers();
initUnauthedUserSession();

const release = (window as any).configs?.BUILD_NUMBER || 'dev';

// Let cross-origin iframes (builder) inherit LogRocket creds and merge into
// this session via the observability handshake. Only install the listener
// when LogRocket is actually enabled here — otherwise a reply would push the
// child into a standalone LogRocket session with no parent to merge into.
if (getEnv('VUE_APP_LOGROCKET_ENABLED') === 'yes') {
  installHostListener(() => ({
    type: HOST_REPLY_TYPE,
    tool: 'logrocket',
    host: 'admin-app',
    logrocket: {
      appID: getEnv('VUE_APP_LOGROCKET_APP_ID') || '',
      parentDomain: window.location.origin,
      ingestServer: getEnv('VUE_APP_LOGROCKET_INGEST_SERVER') || '',
      sdkServer: getEnv('VUE_APP_LOGROCKET_INGEST_SERVER') || ''
    }
  }));
}

logger
  .init({
    release,
    isDev: process.env.NODE_ENV === 'development',
    logrocket: {
      enabled: getEnv('VUE_APP_LOGROCKET_ENABLED') === 'yes',
      appID: getEnv('VUE_APP_LOGROCKET_APP_ID') || '',
      options: {
        release,
        console: {
          isEnabled: true,
          shouldAggregateConsoleErrors: true
        },
        mergeIframes: true,
        childDomains: ['*'],
        ingestServer: getEnv('VUE_APP_LOGROCKET_INGEST_SERVER') || '',
        sdkServer: getEnv('VUE_APP_LOGROCKET_INGEST_SERVER') || ''
      }
    },
    amplitude: {
      enabled: getEnv('VUE_APP_AMPLITUDE_ENABLED') === 'true',
      apiKey: getEnv('VUE_APP_AMPLITUDE_API_KEY') || ''
    }
  })
  .then(() => {
    const currentUser = store.getters['user/getCurrentUser'];
    const currentOrg = store.getters['org/getCurrentOrg'];
    logger.identify(currentUser?.id, {
      name: currentUser?.first_name?.concat(' ', currentUser?.last_name),
      email: currentUser?.email,
      organization: currentUser?.organization
    });
    if (getEnv('VUE_APP_PENDO_ENABLED') === 'true') {
      (window as any).pendo.initialize({
        visitor: {
          id: currentUser?.id,
          email: currentUser?.email,
          firstName: currentUser?.first_name?.concat(' ', currentUser?.last_name)
        },
        account: {
          id: currentUser?.organization,
          name: currentOrg?.name
        }
      });
    }
  })
  .catch(err => {
    logger.error('Error initializing logger', err);
  });

(window as any).logger = logger;

const rawAmplifyConfig = getEnv('VUE_APP_AMPLIFY_CONFIG_OVERRIDES');
if (!rawAmplifyConfig) {
  throw new Error(
    '[CONFIG] Missing VUE_APP_AMPLIFY_CONFIG_OVERRIDES. Authentication will not function without this configuration.'
  );
}
const amplifyConfig = {
  aws_cognito_identity_pool_id: rawAmplifyConfig.AWS_COGNITO_IDENTITY_POOL_ID,
  aws_cognito_region: rawAmplifyConfig.AWS_COGNITO_REGION,
  aws_project_region: rawAmplifyConfig.AWS_PROJECT_REGION,
  aws_user_files_s3_bucket: rawAmplifyConfig.AWS_USER_FILES_S3_BUCKET,
  aws_user_files_s3_bucket_region: rawAmplifyConfig.AWS_USER_FILES_S3_BUCKET_REGION,
  aws_user_pools_id: rawAmplifyConfig.AWS_USER_POOLS_ID,
  aws_user_pools_web_client_id: rawAmplifyConfig.AWS_USER_POOLS_WEB_CLIENT_ID
};

deleteWrongCognitoDomainCookies(amplifyConfig.aws_user_pools_web_client_id);

Amplify.configure(amplifyConfig);

Auth.configure({
  storage: localStorage, // Store tokens in localStorage storage
  cookieStorage: null, // Disable cookie storage
  domain: getEnv('VUE_APP_AUTH_COOKIE_DOMAIN')
});

app.directive('focused', {
  mounted: function (el, binding) {
    if (binding.value !== undefined && binding.value !== false) {
      el.focus();
    }
  }
});

app.directive('studio-click-outside', clickOutsideDirective);

app.use(globalComponents);

//@breaking-change
(window as any).mobileCheck = () => false;
app.use(uicomponents, {
  $trackEvent: () => Function.prototype
});
const i18n = (window as any).i18n;
app.use(i18n);

/* eslint-disable */
app.mixin({
  methods: {
    imageSrcPath: str => `${getEnv('VUE_APP_ASSETS_CDN')}/${str}`
  }
});
/* eslint-enable */

function initInitialAuthState(store: any) {
  // Copy the Cognito cookies to the iframe apps
  window.addEventListener('message', event => {
    if (event.data.type === 'load-builder-cookies') {
      const cognitoIdentityServiceProvider = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('CognitoIdentityServiceProvider')) {
          cognitoIdentityServiceProvider[key] = localStorage.getItem(key);
        }
      }
      const iframeApps = document.querySelectorAll('iframe');
      for (let i = 0; i < iframeApps.length; i++) {
        const iframeApp = iframeApps[i] as HTMLIFrameElement;
        const domains = [
          getEnv('VUE_APP_BUILDER_URL')?.toString() ?? '',
          getEnv('VUE_APP_EVENTHUB_URL')?.toString() ?? ''
        ];
        domains.forEach(domain => {
          iframeApp.contentWindow?.postMessage(
            {
              type: 'builder-cookies-loaded',
              localCookies: cognitoIdentityServiceProvider
            },
            domain
          );
        });
      }
    }
  });
  if (
    window.location.pathname.includes('sso') ||
    window.location.pathname.includes('oktalogin') ||
    (window.location.pathname.includes('/auth/org/') && !getCookieValue('logged_in_via_sso'))
  ) {
    store.commit('user/setSsoLoginInProgress', true);
  }
  return store.dispatch('user/updateCurrentAppUser');
}

const sdkBaseUrl = getEnv('VUE_APP_GOLDCAST_SDK_URL');
if (sdkBaseUrl) {
  const script = document.createElement('script');
  script.src = `${sdkBaseUrl}/v1.0.208_161/embed.js`;
  script.onerror = () => {
    console.error(`[CONFIG] Failed to load SDK from: ${sdkBaseUrl}`);
  };
  document.head.appendChild(script);
}

initInitialAuthState(store).then(() => {
  // eslint-disable-next-line vue/require-name-property
  app.mount('#app');
});
